import moment from 'moment';
import {LeanDocument, Types} from 'mongoose';
import {ToDoManagerApi} from '@/classes/api/to-do-manager.api.class';
import {NotifierApi} from '@/classes/api/notifier.api';
import {ToDoItemDocument} from '@/documentTypes/to-do-manager/ToDoItem.document';
import {NotificationLike} from '@/types/NotificationLike.type';
import {
    completeToDoWithUndo,
    needsCompletionDialog,
    snoozeToDoWithUndo,
    ToDoSnoozePreset,
} from '@/components/toDos/toDoActions.helper';
import {toDoIdOf} from '@/components/app/appBar/notifications/notificationCenter/notificationView.helper';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import store from '@/store';
import {markToDoCompleted} from '@/components/app/appBar/notifications/toDoCompletion.helper';
import {LoginTypes} from '@/enums/LoginTypes.enum';

/**
 * Actions of pop-ups and panel rows (Notify v2, B2/B3/D3):
 * to-do notifications: Erledigen, Morgen erinnern, In 3 Std. (moves the due date of the to-do);
 * every notification on a new notifier: "Später erinnern" (snooze of the notification itself).
 */

export type NotificationSnoozePreset = '1h' | '3h' | 'tomorrow' | 'nextWeek';

export const NOTIFICATION_SNOOZE_OPTIONS: Array<{ value: NotificationSnoozePreset; text: string }> = [
    {value: '1h', text: 'In 1 Std.'},
    {value: '3h', text: 'In 3 Std.'},
    {value: 'tomorrow', text: 'Morgen, 8 Uhr'},
    {value: 'nextWeek', text: 'Nächste Woche, Mo 8 Uhr'},
];

export function snoozeUntil(preset: NotificationSnoozePreset, now = moment()): Date {
    switch (preset) {
        case '1h':
            return now.clone().add(1, 'hour').toDate();
        case '3h':
            return now.clone().add(3, 'hours').toDate();
        case 'tomorrow':
            return now.clone().add(1, 'day').startOf('day').hour(8).toDate();
        default:
            return now.clone().add(1, 'week').startOf('isoWeek').hour(8).toDate();
    }
}

const TO_DO_FIELDS = ['name', 'dataType', 'completed', 'completionComment', 'dueDate', 'hasDueTime'];

/** Quick actions of a to-do notification: "Erledigen" needs update on completed, "Neues Datum" on dueDate (like the to-do rows) */
export function toDoNotificationAccess(notification: NotificationLike): { complete: boolean; snooze: boolean } {
    const id = toDoIdOf(notification);
    // Partner logins have no access to the to-do-manager
    if (!id || store.state.userType === LoginTypes.PARTNER) {
        return {complete: false, snooze: false};
    }
    const entity = store.state.crudAccessManager.entity(CrudEntityTypes.TO_DO_ITEM, id as unknown as Types.ObjectId);
    if (!entity.canRead('completed')) {
        return {complete: false, snooze: false};
    }
    return {complete: entity.canUpdate('completed'), snooze: entity.canUpdate('dueDate')};
}

async function loadToDo(notification: NotificationLike): Promise<LeanDocument<ToDoItemDocument> | undefined> {
    const id = toDoIdOf(notification);
    if (!id) {
        return undefined;
    }
    return ToDoManagerApi.items.findById(id as unknown as Types.ObjectId, {fields: TO_DO_FIELDS});
}

export type CompleteResult = 'completed' | 'alreadyDone' | 'needsDialog' | 'missing';

/**
 * Completes the to-do of a notification with undo. To-dos that expect an answer need the completion dialog
 * (`needsDialog`, the caller opens it with the returned id).
 */
export async function completeFromNotification(notification: NotificationLike): Promise<{ result: CompleteResult; toDoId?: string }> {
    const item = await loadToDo(notification);
    if (!item) {
        return {result: 'missing'};
    }
    const toDoId = String(item._id);
    if (item.completed) {
        markToDoCompleted(toDoId);
        return {result: 'alreadyDone', toDoId};
    }
    if (needsCompletionDialog(item)) {
        return {result: 'needsDialog', toDoId};
    }
    await completeToDoWithUndo(item);
    markToDoCompleted(toDoId);
    return {result: 'completed', toDoId};
}

/** Moves the due date of the to-do ("Morgen erinnern", "In 3 Std.") with undo */
export async function snoozeToDoFromNotification(notification: NotificationLike, preset: ToDoSnoozePreset): Promise<boolean> {
    const item = await loadToDo(notification);
    if (!item || item.completed) {
        return false;
    }
    await snoozeToDoWithUndo(item, preset);
    return true;
}

/** Snoozes notifications on the notifier (new backend only); false when not supported */
export async function snoozeNotifications(ids: Types.ObjectId[], preset: NotificationSnoozePreset): Promise<boolean> {
    return NotifierApi.snooze(ids, snoozeUntil(preset));
}

export function snoozeText(preset: NotificationSnoozePreset): string {
    const until = moment(snoozeUntil(preset));
    return until.isSame(moment(), 'day') ? `Erinnerung um ${until.format('HH:mm')} Uhr` : `Erinnerung ${until.format('dd, DD.MM., HH:mm')} Uhr`;
}
